import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import requests from '../../services/httpServices';

const SchoolSearch = ({ onSchoolSelect, placeholder = "Search for your school..." }) => {
  const [query, setQuery] = useState('');
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchSchools = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSchools([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await requests.get(`/auth/search-schools?query=${encodeURIComponent(searchQuery)}`);

      if (response && response.schools) {
        setSchools(response.schools);
        setShowResults(true);
      } else {
        setError('No schools found');
        setSchools([]);
        setShowResults(false);
      }
    } catch (err) {
      setError('Failed to search schools');
      setSchools([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce the search
    clearTimeout(searchRef.current?.timeout);
    searchRef.current.timeout = setTimeout(() => {
      searchSchools(value);
    }, 300);
  };

  const handleSchoolSelect = (school) => {
    onSchoolSelect(school);
    setQuery(school.name);
    setShowResults(false);
  };

  return (
    <div className="school-search-container" ref={searchRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="form-control"
        autoComplete="off"
      />

      {loading && (
        <div className="search-loading" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ddd',
          borderTop: 'none',
          padding: '10px',
          zIndex: 1000
        }}>
          <small>Searching...</small>
        </div>
      )}

      {error && (
        <div className="search-error" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ddd',
          borderTop: 'none',
          padding: '10px',
          zIndex: 1000
        }}>
          <small className="text-danger">{error}</small>
        </div>
      )}

      {showResults && schools.length > 0 && (
        <div className="school-results" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ddd',
          borderTop: 'none',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {schools.map(school => (
            <div
              key={school._id}
              className="school-option"
              onClick={() => handleSchoolSelect(school)}
              style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <div className="school-name" style={{ fontWeight: 'bold' }}>{school.name}</div>
              <div className="school-address" style={{ fontSize: '0.9em', color: '#666' }}>{school.address}</div>
            </div>
          ))}
        </div>
      )}

      {showResults && schools.length === 0 && query.length >= 2 && !loading && (
        <div className="no-results" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ddd',
          borderTop: 'none',
          padding: '10px',
          zIndex: 1000
        }}>
          <small>No schools found matching &quot;{query}&quot;</small>
        </div>
      )}
    </div>
  );
};

SchoolSearch.propTypes = {
  onSchoolSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

export default SchoolSearch;
